import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDatabase } from '../../hooks/useDatabase';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PriorityItemsList from '../../components/priorities/PriorityItemsList';
import * as wasm from 'sched';

export function PrioritiesPage() {
  const { database, loading: dbLoading } = useDatabase();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const owners: { type_: string; id: string; label: string }[] = useMemo(() => {
    if (!database) return [];
    const db = database as unknown as wasm.WrappedDatabase;
    const rawOwners: { type_: string; id: string }[] = (() => {
      try {
        const result = db.priority_item_owners();
        return Array.isArray(result) ? result : [];
      } catch {
        return [];
      }
    })();

    return rawOwners.map((o) => {
      let label = `${o.type_} ${o.id.slice(0, 8)}`;
      try {
        if (o.type_ === 'Team') {
          const team = db.teams()[o.id];
          if (team) label = team.name();
        } else if (o.type_ === 'Person') {
          const person = db.people()[o.id];
          if (person) label = person.name();
        }
      } catch { /* fallback to default label */ }
      return { ...o, label };
    });
  }, [database]);

  if (dbLoading || !database) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-mono font-semibold text-cm-text-primary">Priorities</h1>
        <p className="mt-1 text-sm text-cm-text-secondary">
          Each team or person can maintain their own priority ordering to guide forecasting.
        </p>
      </div>

      {owners.length === 0 ? (
        <div className="rounded-xl border border-dashed border-cm-border-mid bg-cm-bg-surface p-6 text-center text-sm text-cm-text-muted">
          No priorities set yet. Add priorities from a team dashboard.
        </div>
      ) : (
        owners.map((owner) => (
          <div key={`${owner.type_}-${owner.id}`} className="rounded-xl border border-cm-border-subtle bg-cm-bg-surface p-6">
            <h2 className="text-lg font-mono font-medium text-cm-text-primary mb-3">
              <span className="text-xs uppercase text-cm-text-muted mr-2">{owner.type_}</span>
              {owner.label}
            </h2>
            <PriorityItemsList
              database={database as unknown as wasm.WrappedDatabase}
              workspaceId={workspaceId}
              owner={{ type_: owner.type_, id: owner.id }}
              editable
            />
          </div>
        ))
      )}
    </div>
  );
}
